import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  ArrowLeftRight,
  GitBranch,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  Activity,
  Layers,
  Database,
  Calendar,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (BACKEND_URL && !BACKEND_URL.startsWith("http")) {
  BACKEND_URL = `https://${BACKEND_URL}`;
}
if (!BACKEND_URL) {
  BACKEND_URL = window.location.origin;
}
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  success: { color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Réussi" },
  partial: { color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Partiel" },
  failed: { color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200", label: "Échoué" },
  in_progress: { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "En cours" },
};

function formatObjectType(objectType, objectMappings) {
  if (!objectType) return objectType;
  const parts = objectType.split("_", 2);
  if (parts.length === 2) {
    const match = objectMappings?.find(
      (m) => m.hubspot_object === parts[0] && m.bc_entity === parts[1]
    );
    if (match) {
      return `${match.hubspot_object_label} ↔ ${match.bc_entity_label}`;
    }
  }
  return objectType;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const connections = stats?.connections || {};
  const mappings = stats?.mappings || {};
  const objectMappings = stats?.object_mappings || [];
  const syncs = stats?.syncs || {};
  const recentSyncs = syncs.recent || [];
  const schedules = stats?.schedules || {};

  const bothConnected = connections.hubspot && connections.business_central;
  const syncSuccessRate =
    syncs.total > 0
      ? Math.round(((syncs.successful + (syncs.partial || 0)) / syncs.total) * 100)
      : 0;

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Vue d'ensemble de vos synchronisations HubSpot ↔ Business Central
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats}>
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Actualiser
        </Button>
      </div>

      {/* Connection Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={`border-l-4 ${connections.hubspot ? "border-l-[#FF7A59]" : "border-l-slate-300"}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connections.hubspot ? "bg-[#FF7A59]/10" : "bg-slate-100"}`}>
                  <span className="text-lg font-bold" style={{ color: connections.hubspot ? "#FF7A59" : "#94a3b8" }}>H</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">HubSpot</p>
                  <p className="text-xs text-slate-500">Private App</p>
                </div>
              </div>
              {connections.hubspot ? (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Connecté
                </Badge>
              ) : (
                <Link to="/settings">
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200 gap-1 cursor-pointer hover:bg-amber-100">
                    <AlertCircle className="w-3 h-3" /> Configurer
                  </Badge>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${connections.business_central ? "border-l-[#00B7C3]" : "border-l-slate-300"}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connections.business_central ? "bg-[#00B7C3]/10" : "bg-slate-100"}`}>
                  <span className="text-lg font-bold" style={{ color: connections.business_central ? "#00B7C3" : "#94a3b8" }}>BC</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Business Central</p>
                  <p className="text-xs text-slate-500">OAuth2</p>
                </div>
              </div>
              {connections.business_central ? (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Connecté
                </Badge>
              ) : (
                <Link to="/settings">
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200 gap-1 cursor-pointer hover:bg-amber-100">
                    <AlertCircle className="w-3 h-3" /> Configurer
                  </Badge>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Layers className="w-4.5 h-4.5 text-indigo-600" />
              </div>
              <Link to="/mapping">
                <ArrowUpRight className="w-4 h-4 text-slate-400 hover:text-indigo-600 transition-colors" />
              </Link>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900">{objectMappings.length}</p>
            <p className="text-xs text-slate-500 mt-0.5">Object Mappings</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <GitBranch className="w-4.5 h-4.5 text-emerald-600" />
              </div>
              <Link to="/mapping">
                <ArrowUpRight className="w-4 h-4 text-slate-400 hover:text-emerald-600 transition-colors" />
              </Link>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900">
              {mappings.active || 0}<span className="text-base font-normal text-slate-400">/{mappings.total || 0}</span>
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Champs actifs</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <Activity className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <Link to="/history">
                <ArrowUpRight className="w-4 h-4 text-slate-400 hover:text-blue-600 transition-colors" />
              </Link>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900">{syncs.total || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Synchronisations</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                <TrendingUp className="w-4.5 h-4.5 text-teal-600" />
              </div>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900">
              {syncSuccessRate}<span className="text-base font-normal text-slate-400">%</span>
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Taux de réussite</p>
          </CardContent>
        </Card>
      </div>

      {/* Object Mappings & Recent Syncs */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Object Mappings */}
        <div className="lg:col-span-3 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Layers className="w-4.5 h-4.5 text-indigo-600" />
              Object Mappings
            </h2>
            <Link to="/mapping">
              <Button variant="ghost" size="sm" className="text-xs gap-1">
                Gérer <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>

          {objectMappings.length > 0 ? (
            <div className="space-y-2.5">
              {objectMappings.map((om) => (
                <Card key={om.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#FF7A59]" />
                            <span className="text-sm font-medium text-slate-900">
                              {om.hubspot_object_label}
                            </span>
                          </div>
                          <ArrowLeftRight className="w-3.5 h-3.5 text-slate-400" />
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#00B7C3]" />
                            <span className="text-sm font-medium text-slate-900">
                              {om.bc_entity_label}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <GitBranch className="w-3.5 h-3.5" />
                          <span className="text-xs">{om.field_count} champ{om.field_count !== 1 ? "s" : ""}</span>
                        </div>
                        <Link to="/data-browser">
                          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-slate-500 hover:text-indigo-600">
                            <Database className="w-3.5 h-3.5" />
                            Explorer
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-10 text-center">
                <Layers className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 font-medium">Aucun mapping configuré</p>
                <p className="text-sm text-slate-400 mt-1">Commencez par créer un mapping d'objets</p>
                <Link to="/mapping">
                  <Button variant="outline" className="mt-4" size="sm">
                    <GitBranch className="w-4 h-4 mr-1.5" />
                    Configurer
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Syncs */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Activity className="w-4.5 h-4.5 text-blue-600" />
              Activité récente
            </h2>
            <Link to="/history">
              <Button variant="ghost" size="sm" className="text-xs gap-1">
                Tout voir <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>

          <Card>
            <CardContent className="p-0">
              {recentSyncs.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {recentSyncs.slice(0, 8).map((sync, index) => {
                    const cfg = statusConfig[sync.status] || statusConfig.failed;
                    return (
                      <div key={sync.id || index} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-7 h-7 rounded-full ${cfg.bg} flex items-center justify-center shrink-0`}>
                            {sync.status === "success" ? (
                              <CheckCircle2 className={`w-3.5 h-3.5 ${cfg.color}`} />
                            ) : sync.status === "failed" ? (
                              <XCircle className={`w-3.5 h-3.5 ${cfg.color}`} />
                            ) : sync.status === "in_progress" ? (
                              <RefreshCw className={`w-3.5 h-3.5 ${cfg.color} animate-spin`} />
                            ) : (
                              <AlertCircle className={`w-3.5 h-3.5 ${cfg.color}`} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {formatObjectType(sync.object_type, objectMappings)}
                            </p>
                            <p className="text-xs text-slate-400">
                              {timeAgo(sync.started_at)}
                              {sync.sync_type === "manual" && " · Manuel"}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={`${cfg.bg} ${cfg.color} ${cfg.border} text-[10px] shrink-0`}>
                          {cfg.label}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10">
                  <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Aucune synchronisation</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Lancez une sync depuis Paramètres
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sync breakdown mini-stats */}
          {syncs.total > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-emerald-50 px-3 py-2.5 text-center">
                <p className="text-lg font-bold text-emerald-700">{syncs.successful || 0}</p>
                <p className="text-[10px] text-emerald-600">Réussies</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2.5 text-center">
                <p className="text-lg font-bold text-amber-700">{syncs.partial || 0}</p>
                <p className="text-[10px] text-amber-600">Partielles</p>
              </div>
              <div className="rounded-lg bg-rose-50 px-3 py-2.5 text-center">
                <p className="text-lg font-bold text-rose-700">{syncs.failed || 0}</p>
                <p className="text-[10px] text-rose-600">Échouées</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/mapping" className="group">
          <Card className="hover:shadow-md hover:border-indigo-200 transition-all h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                <GitBranch className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Field Mapping</p>
                <p className="text-xs text-slate-400 truncate">Configurer les champs</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/data-browser" className="group">
          <Card className="hover:shadow-md hover:border-teal-200 transition-all h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                <Database className="w-4 h-4 text-teal-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Data Browser</p>
                <p className="text-xs text-slate-400 truncate">Explorer les données</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/history" className="group">
          <Card className="hover:shadow-md hover:border-blue-200 transition-all h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <Clock className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Historique</p>
                <p className="text-xs text-slate-400 truncate">Voir les syncs</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings" className="group">
          <Card className="hover:shadow-md hover:border-slate-300 transition-all h-full">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                <Calendar className="w-4 h-4 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Paramètres</p>
                <p className="text-xs text-slate-400 truncate">
                  {schedules.active || 0} planification{(schedules.active || 0) !== 1 ? "s" : ""} active{(schedules.active || 0) !== 1 ? "s" : ""}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
